import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { cn } from '@/lib/utils/cn';
import { Download, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { useDownloadReports } from '../api';
import type { ReportDownloadRow } from '../types';

const STATUS_LABEL: Record<string, { label: string; tone: 'neutral' | 'pending' | 'ready' }> = {
  '0': { label: 'Initiated', tone: 'pending' },
  '1': { label: 'Processing', tone: 'pending' },
  '2': { label: 'Ready', tone: 'ready' },
};

function statusBadge(status: string | undefined) {
  const meta = STATUS_LABEL[String(status ?? '')] ?? { label: '—', tone: 'neutral' as const };
  return (
    <Badge
      variant="outline"
      className={cn(
        'font-normal',
        meta.tone === 'pending' && 'border-amber-200 bg-amber-50 text-amber-900',
        meta.tone === 'ready' && 'border-emerald-200 bg-emerald-50 text-emerald-900',
      )}
    >
      {meta.label}
    </Badge>
  );
}

export function DownloadReportPage() {
  const isSubOrSuper = useAuthStore((s) => s.isSubOrSuperAdmin());
  const query = useDownloadReports();
  const rows = query.data ?? [];

  const columns = useMemo<ColumnDef<ReportDownloadRow>[]>(() => {
    const cols: ColumnDef<ReportDownloadRow>[] = [
      {
        accessorKey: 'campaignId',
        header: 'Campaign ID',
        size: 130,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.campaignId ?? '—'}
          </span>
        ),
      },
      { accessorKey: 'campaignName', header: 'Campaign', size: 240 },
    ];

    if (isSubOrSuper) {
      cols.push({ accessorKey: 'username', header: 'Owner', size: 160 });
    }

    cols.push(
      { accessorKey: 'reqDate', header: 'Requested', size: 180 },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 130,
        cell: ({ row }) => statusBadge(row.original.status as string),
      },
      { accessorKey: 'reportType', header: 'Type', size: 130 },
      {
        id: 'download',
        header: 'Settings',
        size: 140,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          if (String(r.status) !== '2' || !r.reportUrl) {
            return <span className="text-xs text-muted-foreground">Not ready</span>;
          }
          return (
            <Button asChild size="sm" variant="outline">
              <a href={r.reportUrl} target="_blank" rel="noopener noreferrer">
                <Download className="h-4 w-4" />
                Download
              </a>
            </Button>
          );
        },
      },
    );

    return cols;
  }, [isSubOrSuper]);

  return (
    <FillPage>
      <PageHeader
        title="Downloads"
        description="Generated campaign reports — click a row to download once it's ready."
        actions={
          <Button
            variant="outline"
            size="icon"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
            aria-label="Refresh"
            title="Refresh"
          >
            <RefreshCw className={cn('h-4 w-4', query.isFetching && 'animate-spin')} />
          </Button>
        }
      />

      <DataTable<ReportDownloadRow, unknown>
        tableId="reports:download:v1"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search reports…"
        emptyTitle="No reports yet"
        emptyDescription="Reports you generate from the Summary screens will appear here."
        getRowId={(row, i) => `${row.campaignId ?? 'noid'}:${row.reqDate ?? i}`}
        initialColumnPinning={{ right: ['download'] }}
        exportFilename="reports.csv"
        pageSize={10}
      />
    </FillPage>
  );
}
